import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePermissions } from '../lib/PermissionsContext';
import { hasPermission } from '../lib/permissions';
import { version } from '../../package.json';
import { FeedbackDialog } from './FeedbackDialog';
import { API_ROUTES } from '../lib/api-routes';
import { branding } from '../../config/branding';
import type { UserProfile } from '../types';

/**
 * Convert hex color to rgba with specified opacity (0-1)
 * @param hex - Hex color (e.g., '#FF0000' or 'FF0000')
 * @param opacity - Opacity value 0-1 (e.g., 0.6)
 * @returns rgba color string (defaults to transparent black if invalid hex)
 */
function hexToRgba(hex: string, opacity: number): string {
  // Remove '#' if present
  const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex;
  // Validate hex format (must be exactly 6 characters)
  if (cleanHex.length !== 6) {
    return `rgba(0, 0, 0, ${opacity})`;
  }
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  // Validate parsed values (check for NaN)
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return `rgba(0, 0, 0, ${opacity})`;
  }
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// Navigation structure with icons and routes
interface NavChild {
  id: string;
  label: string;
  path: string;
  permission: string;
}

interface NavParent {
  id: string;
  label: string;
  icon: string;
  children: NavChild[];
}

// Main navigation sections (displayed in scrollable area)
const navigationStructure: NavParent[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: '📊',
    children: [
      { id: 'overview', label: 'Overview', path: '/dashboard/overview', permission: 'dashboard.overview.read' },
      { id: 'financial', label: 'Financial Dashboard', path: '/dashboard/financial', permission: 'dashboard.financial.read' },
      { id: 'kanban', label: 'Kanban Board', path: '/dashboard/kanban', permission: 'dashboard.kanban.read' },
    ],
  },
  {
    id: 'oms',
    label: 'Orders',
    icon: '📦',
    children: [
      { id: 'orders', label: 'Orders', path: '/orders/orders', permission: 'oms.orders.read' },
      { id: 'customers', label: 'Customers', path: '/orders/customers', permission: 'oms.customers.read' },
      { id: 'products', label: 'Products', path: '/orders/products', permission: 'oms.products.read' },
      { id: 'coupons', label: 'Coupons', path: '/orders/coupons', permission: 'oms.coupons.read' },
      { id: 'sales-config', label: 'Sales Config', path: '/orders/inventory', permission: 'oms.sales-config.read' },
    ],
  },
  {
    id: 'tracking',
    label: 'Tracking',
    icon: '⏱️',
    children: [
      { id: 'time-tracking', label: 'Time Tracking', path: '/tracking/time', permission: 'oms.time-tracking.read' },
      { id: 'mileage', label: 'Mileage', path: '/tracking/mileage', permission: 'oms.mileage.read' },
    ],
  },
  {
    id: 'workshop',
    label: 'Workshop',
    icon: '🏭',
    children: [
      { id: 'printers', label: 'Printers', path: '/workshop/printers', permission: 'workshop.printers.read' },
      { id: 'spools', label: 'Filament & Spools', path: '/workshop/spools', permission: 'workshop.spools.read' },
      { id: 'print-queue', label: 'Print Queue', path: '/workshop/print-queue', permission: 'workshop.print-queue.read' },
      { id: 'maintenance', label: 'Maintenance', path: '/workshop/maintenance', permission: 'workshop.maintenance.read' },
      { id: 'inventory', label: 'Supplies', path: '/workshop/inventory', permission: 'workshop.inventory.read' },
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: '🏦',
    children: [
      { id: 'journal', label: 'Journal Entries', path: '/finance/journal', permission: 'finance.journal.read' },
      { id: 'transactions', label: 'General Ledger', path: '/finance/transactions', permission: 'finance.transactions.read' },
      { id: 'coa', label: 'Chart of Accounts', path: '/finance/coa', permission: 'finance.coa.read' },
      { id: 'reports', label: 'Reports', path: '/finance/reports', permission: 'finance.reports.read' },
      { id: 'reconciliation', label: 'Reconciliation', path: '/finance/reconciliation', permission: 'finance.reconciliation.read' },
      { id: 'import', label: 'Import', path: '/finance/import', permission: 'finance.import.read' },
    ],
  },
  {
    id: 'enhanced-finance',
    label: 'Enhanced Finance',
    icon: '💼',
    children: [
      { id: 'ef-dashboard', label: 'Dashboard', path: '/finance/enhanced/dashboard', permission: 'finance/enhanced/dashboard.read' },
      { id: 'ef-journal', label: 'Journal Entries', path: '/finance/enhanced/entries/journal', permission: 'finance/enhanced/entries/journal.read' },
      { id: 'ef-posted', label: 'Posted Entries', path: '/finance/enhanced/entries/posted', permission: 'finance/enhanced/entries/posted.read' },
      { id: 'ef-templates', label: 'Entry Templates', path: '/finance/enhanced/entries/templates', permission: 'finance/enhanced/entries/templates.read' },
      { id: 'ef-coa', label: 'Chart of Accounts', path: '/finance/enhanced/accounts/coa', permission: 'finance/enhanced/accounts/coa.read' },
      { id: 'ef-reconciliation', label: 'Account Reconciliation', path: '/finance/enhanced/accounts/reconciliation', permission: 'finance/enhanced/accounts/reconciliation.read' },
      { id: 'ef-periods', label: 'Period Management', path: '/finance/enhanced/periods', permission: 'finance/enhanced/periods.read' },
      { id: 'ef-reports', label: 'Reports', path: '/finance/enhanced/reports', permission: 'finance/enhanced/reports.read' },
      { id: 'ef-balance-history', label: 'Account Balance History', path: '/finance/enhanced/reports/balance-history', permission: 'finance/enhanced/reports.read' },
      { id: 'ef-operations', label: 'Batch Operations', path: '/finance/enhanced/operations', permission: 'finance/enhanced/operations.read' },
    ],
  },
];

// Admin section (pinned to bottom of sidebar, above version footer)
const adminSection: NavParent = {
  id: 'admin',
  label: 'Admin',
  icon: '⚙️',
  children: [
     { id: 'etsy', label: 'Etsy Integration', path: '/admin/etsy', permission: 'etsy.integration.read' },
     { id: 'etsy-import', label: 'Etsy CSV Import', path: '/admin/etsy-import', permission: 'etsy.import.read' },
     { id: 'audit', label: 'Audit Log', path: '/admin/audit', permission: 'admin.audit.read' },
     { id: 'users', label: 'Users', path: '/admin/users', permission: 'admin.users.read' },
     { id: 'roles', label: 'Roles & Permissions', path: '/admin/roles', permission: 'admin.roles.read' },
     { id: 'profile', label: 'Profile', path: '/admin/profile', permission: 'admin.profile.read' },
     { id: 'settings', label: 'Settings', path: '/admin/settings', permission: 'admin.settings.read' },
  ],
};

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ isCollapsed, onToggle }: SidebarProps) {
  const location = useLocation();
  const { permissions } = usePermissions();
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  
  // Get user info for feedback submission
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  
  // Fetch user profile data on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const res = await fetch(API_ROUTES.profile.get());
        if (res.ok) {
          const data = (await res.json()) as { profile: UserProfile };
          const profile = data.profile;
          setUserEmail(profile.email || '');
          setUserName(profile.displayName || profile.email || 'User');
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        // Fallback: silently set defaults, feedback form will show email if available
        setUserEmail('');
        setUserName('User');
      }
    };
    
    fetchUserProfile();
  }, []);
  
  // Track which parent sections are expanded - stored in localStorage
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    // eslint-disable-next-line no-undef
    const stored = localStorage.getItem('sidebar-expanded-sections');
    if (stored) {
      try {
        return new Set(JSON.parse(stored));
      } catch {
        return new Set(['orders']); // Default to orders expanded
      }
    }
    return new Set(['orders']);
  });
  
  // Track the previous parent section to avoid reading expandedSections in the effect
  const prevParentSectionRef = useRef<string | null>(null);

  // Memoize the first path segment to avoid creating new array on every render
  const firstPathSegment = useMemo(() => {
    // Special-case Enhanced Finance: its sidebar section id does not match the first URL segment.
    // Without this, navigating to /finance/enhanced/* would only auto-expand the Finance section.
    if (location.pathname.startsWith('/finance/enhanced')) {
      return 'enhanced-finance';
    }

    const pathParts = location.pathname.split('/').filter(Boolean);
    return pathParts.length > 0 ? pathParts[0] : null;
  }, [location.pathname]);

  // Persist expanded sections to localStorage
  useEffect(() => {
    // eslint-disable-next-line no-undef
    localStorage.setItem('sidebar-expanded-sections', JSON.stringify(Array.from(expandedSections)));
  }, [expandedSections]);

  // Auto-expand parent section when navigating to a child route
  // Note: expandedSections is intentionally omitted from the dependency array to prevent infinite loops.
  // Including it would cause: navigation changes expandedSections -> effect runs -> updates expandedSections -> repeat
  // Instead, we use useRef to track the previous parent section, which allows us to:
  // 1. Avoid stale closures by reading the current state via the setter's callback (prev => ...)
  // 2. Only trigger expansion on actual navigation changes (firstPathSegment changes)
  // 3. Prevent redundant expansions when the user manually toggles sections
  // This pattern is preferred over alternatives like:
  // - Adding expandedSections to deps: causes infinite loop
  // - Using a separate flag state: adds unnecessary complexity and re-renders
  // - Checking inside setExpandedSections without ref: would still expand on every navigation to same parent
  useEffect(() => {
    if (firstPathSegment) {
      // Only expand if we've changed to a different parent section
      if (firstPathSegment !== prevParentSectionRef.current) {
        prevParentSectionRef.current = firstPathSegment;
        setExpandedSections(prev => {
          if (!prev.has(firstPathSegment)) {
            return new Set([...prev, firstPathSegment]);
          }
          return prev;
        });
      }
    }
  }, [firstPathSegment]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const isChildActive = (path: string) => {
    return location.pathname === path;
  };

  const isParentActive = (parentId: string) => {
    if (parentId === 'enhanced-finance') {
      return location.pathname.startsWith('/finance/enhanced');
    }

    return location.pathname.startsWith(`/${parentId}`);
  };

  // Helper function to render a navigation section (reusable for both main nav and admin)
  const renderNavSection = (parent: NavParent) => {
    // Check if any child in this parent has permission
    const hasAnyChildAccess = parent.children.some(child => 
      hasPermission(permissions, child.permission)
    );
    if (!hasAnyChildAccess) {
      return null;
    }

    const isExpanded = expandedSections.has(parent.id);
    const isActive = isParentActive(parent.id);

    return (
      <div key={parent.id} className="mb-2">
        {/* Parent Section Button */}
        <button
          onClick={() => toggleSection(parent.id)}
          aria-expanded={isExpanded}
          aria-controls={`section-${parent.id}`}
          className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
            ${isActive ? 'text-white' : 'text-slate-300'}
            ${isCollapsed ? 'justify-center' : ''}
          `}
          style={isActive 
            ? { backgroundColor: hexToRgba(branding.primaryColor, 0.6) }
            : { backgroundColor: hexToRgba(branding.primaryColor, 0.1) }
          }
          onMouseEnter={(e) => {
            if (!isActive) {
              e.currentTarget.style.backgroundColor = hexToRgba(branding.primaryColor, 0.2);
            }
          }}
          onMouseLeave={(e) => {
            if (!isActive) {
              e.currentTarget.style.backgroundColor = hexToRgba(branding.primaryColor, 0.1);
            }
          }}
          title={isCollapsed ? parent.label : undefined}
        >
          <span className="text-xl flex-shrink-0">{parent.icon}</span>
          {!isCollapsed && (
            <>
              <span className="flex-1 text-left font-medium">{parent.label}</span>
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </>
          )}
        </button>

        {/* Child Links */}
        {isExpanded && !isCollapsed && (
          <div id={`section-${parent.id}`} className="ml-4 mt-1 space-y-1 border-l-2 border-slate-700 pl-4">
            {parent.children.map(child => {
              // Check permission for this child
              const hasChildAccess = hasPermission(permissions, child.permission);
              if (!hasChildAccess) {return null;}

              const isChildItemActive = isChildActive(child.path);

              return (
                <Link
                  key={child.id}
                  to={child.path}
                  className={`
                    block px-3 py-2 rounded-lg text-sm transition-colors
                    ${isChildItemActive 
                      ? 'text-white font-medium' 
                      : 'text-slate-300'
                    }
                  `}
                  style={isChildItemActive 
                    ? { backgroundColor: hexToRgba(branding.primaryColor, 0.6) }
                    : {}
                  }
                  onMouseEnter={(e) => {
                    if (!isChildItemActive) {
                      e.currentTarget.style.backgroundColor = hexToRgba(branding.primaryColor, 0.2);
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isChildItemActive) {
                      e.currentTarget.style.backgroundColor = '';
                    }
                  }}
                >
                  {child.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`
        fixed top-0 left-0 h-screen text-white transition-all duration-300 z-40 flex flex-col
        ${isCollapsed ? 'w-16' : 'w-64'}
      `}
      style={{ backgroundColor: branding.secondaryColor }}
    >
      {/* Sidebar Header with Toggle */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: hexToRgba(branding.secondaryColor, 0.3) }}>
        {!isCollapsed && (
          <span className="font-semibold text-sm truncate text-slate-300 uppercase tracking-wider">Navigation</span>
        )}
        <button
          onClick={onToggle}
          className="p-2 rounded-lg transition-colors ml-auto hover:opacity-75"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isCollapsed ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            )}
          </svg>
        </button>
      </div>

      {/* Main Navigation - scrollable area */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {navigationStructure.map(parent => renderNavSection(parent))}
      </nav>

      {/* Admin Section - pinned to bottom */}
      {!isCollapsed && adminSection.children.some(child => hasPermission(permissions, child.permission)) && (
        <div className="border-t px-2 py-2" style={{ borderColor: `${branding.primaryColor}40` }}>
          {renderNavSection(adminSection)}
        </div>
      )}

      {/* Sidebar Footer - version auto-updates from package.json */}
      {!isCollapsed && (
        <div className="border-t p-4 space-y-3" style={{ borderColor: `${branding.primaryColor}40` }}>
          {/* Feedback Button */}
          <button
            onClick={() => setShowFeedbackDialog(true)}
            className="w-full px-3 py-2 text-white text-sm rounded-md font-medium transition-colors hover:opacity-90"
            style={{ backgroundColor: branding.primaryColor }}
            title="Send feedback or report a bug"
          >
            💬 Send Feedback
          </button>
          
          {/* Version and Help */}
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>v{version}</span>
            <Link 
              to="/help"
              className="hover:text-slate-200 transition-colors"
              title="View help documentation"
            >
              Help
            </Link>
          </div>
        </div>
      )}

      {/* Feedback Dialog */}
      <FeedbackDialog
        isOpen={showFeedbackDialog}
        onClose={() => setShowFeedbackDialog(false)}
        userName={userName}
        userEmail={userEmail}
      />
    </aside>
  );
}
